import React, { useState } from 'react';

// ===== components/shared.jsx =====
// Shared UI primitives for LIS Techs onboarding flow
// Original design — not based on any existing branded UI

const BRAND = {
  greenDark: '#0F6E56',
  green: '#1D9E75',
  greenGlow: '#3CC9A3',
  blue: '#3B82F6',
  text: '#1A1A18',
  muted: '#888780',
  surface: '#FDFCF9',
  surfaceAlt: '#F7F6F1',
  mint: '#E1F5EE',
  border: '#E8E5DD',
  error: '#E24B4A',
};

// ─── Logo (original mark) ───────────────────────────────
function Logo({ size = 36, showText = true }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
        <defs>
          <linearGradient id="lg-grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#0F6E56" />
            <stop offset="100%" stopColor="#3CC9A3" />
          </linearGradient>
        </defs>
        {/* Abstract flask glyph — neck rectangle + rounded body */}
        <rect x="15" y="4" width="10" height="8" rx="1.5" fill="url(#lg-grad)" />
        <path d="M10 14 L30 14 L33 32 Q33 36 28 36 L12 36 Q7 36 7 32 Z" fill="url(#lg-grad)" />
        {/* Data dots */}
        <circle cx="15" cy="26" r="1.6" fill="#fff" opacity="0.95" />
        <circle cx="21" cy="22" r="1.6" fill="#fff" opacity="0.95" />
        <circle cx="26" cy="28" r="1.6" fill="#fff" opacity="0.95" />
        <circle cx="18" cy="31" r="1.2" fill="#fff" opacity="0.7" />
      </svg>
      {showText && (
        <div style={{ lineHeight: 1, fontWeight: 700, fontSize: 19, letterSpacing: -0.3 }}>
          <span style={{ color: BRAND.greenDark }}>LIS</span>
          <span style={{ color: BRAND.green }}>techs</span>
        </div>
      )}
    </div>
  );
}

// ─── Icon set (line, original) ──────────────────────────
const Icon = {
  Check: ({ size = 16, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 10.5l3.5 3.5L16 5.5" />
    </svg>
  ),
  Arrow: ({ size = 16, dir = 'left' }) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      style={{ transform: dir === 'left' ? 'none' : 'scaleX(-1)' }}>
      <path d="M12 4l-6 6 6 6" />
    </svg>
  ),
  Plus: ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M10 4v12M4 10h12"/></svg>
  ),
  X: ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M5 5l10 10M15 5L5 15"/></svg>
  ),
  Eye: ({ size = 16, off }) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1.5 10s3-6 8.5-6 8.5 6 8.5 6-3 6-8.5 6S1.5 10 1.5 10z"/>
      <circle cx="10" cy="10" r="2.5"/>
      {off && <path d="M3 3l14 14" stroke="currentColor" strokeWidth="1.7"/>}
    </svg>
  ),
  Upload: ({ size = 24 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3v13M7 8l5-5 5 5"/><path d="M4 17v3a1 1 0 001 1h14a1 1 0 001-1v-3"/>
    </svg>
  ),
  Bank: ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-5 9 5"/><path d="M4 9v10M8 9v10M12 9v10M16 9v10M20 9v10"/><path d="M2 20h20"/>
    </svg>
  ),
  Clock: ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>
    </svg>
  ),
  Branch: ({ size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="6" cy="6" r="2"/><circle cx="18" cy="6" r="2"/><circle cx="12" cy="18" r="2"/><path d="M6 8v4h12V8M12 12v4"/>
    </svg>
  ),
  Shield: ({ size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z"/>
    </svg>
  ),
  Copy: ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <rect x="6" y="6" width="10" height="10" rx="1.5"/><path d="M4 14V5a1 1 0 011-1h9"/>
    </svg>
  ),
  Spark: ({ size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l2 6 6 2-6 2-2 6-2-6-6-2 6-2z"/>
    </svg>
  ),
  Crown: ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7l4 4 5-7 5 7 4-4v11H3z"/>
    </svg>
  ),
  Flask: ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 3h6M10 3v7l-5 9a2 2 0 002 3h10a2 2 0 002-3l-5-9V3"/>
    </svg>
  ),
  Search: ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="9" cy="9" r="5"/><path d="M13 13l4 4"/></svg>
  ),
};

// ─── Button ───────────────────────────────────────────────
function Button({ children, variant = 'primary', size = 'md', onClick, disabled, full, icon, iconLeft, type = 'button', style = {} }) {
  const base = {
    border: 'none', borderRadius: 12, cursor: disabled ? 'not-allowed' : 'pointer',
    fontWeight: 600, fontFamily: 'inherit', letterSpacing: -0.1,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    transition: 'all .18s ease', width: full ? '100%' : 'auto',
    fontSize: size === 'sm' ? 13 : 15, padding: size === 'sm' ? '8px 14px' : '13px 22px',
    opacity: disabled ? 0.5 : 1,
  };
  const variants = {
    primary: {
      background: `linear-gradient(135deg, ${BRAND.greenDark} 0%, ${BRAND.green} 100%)`,
      color: '#fff',
      boxShadow: disabled ? 'none' : '0 8px 24px rgba(29, 158, 117, 0.25)',
    },
    outline: {
      background: '#fff', color: BRAND.text,
      boxShadow: `inset 0 0 0 1px ${BRAND.border}`,
    },
    ghost: { background: 'transparent', color: BRAND.text },
    danger: { background: BRAND.error, color: '#fff' },
  };
  return (
    <button type={type} onClick={disabled ? undefined : onClick} disabled={disabled}
      style={{ ...base, ...variants[variant], ...style }}
      onMouseEnter={(e) => { if (!disabled && variant === 'primary') e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}>
      {iconLeft}
      {children}
      {icon}
    </button>
  );
}

// ─── Field (input wrapper) ──────────────────────────────
function Field({ label, hint, error, optional, children, required }) {
  return (
    <label style={{ display: 'block', marginBottom: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 7 }}>
        <span style={{ fontSize: 13, fontWeight: 500, color: BRAND.text }}>
          {label}
          {required && <span style={{ color: BRAND.error, marginInlineStart: 4 }}>*</span>}
        </span>
        {optional && <span style={{ fontSize: 11, color: BRAND.muted }}>اختياري</span>}
      </div>
      {children}
      {hint && !error && <div style={{ fontSize: 11.5, color: BRAND.muted, marginTop: 6 }}>{hint}</div>}
      {error && <div role="alert" style={{ fontSize: 12, color: BRAND.error, marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
        <span style={{ width: 4, height: 4, borderRadius: 2, background: BRAND.error }}/>{error}
      </div>}
    </label>
  );
}

// Normalises Arabic-Indic (٠-٩) and Persian (۰-۹) digits to ASCII (0-9).
// Applied to every Input value below so users can type رقم الجوال in Arabic
// digits and downstream filters / regex (`\d`, `\D`) keep working without
// per-field changes. Non-numeric characters pass through untouched.
const AR_DIGITS = '٠١٢٣٤٥٦٧٨٩';
const FA_DIGITS = '۰۱۲۳۴۵۶۷۸۹';
function toAsciiDigits(s) {
  if (s == null) return s;
  return String(s)
    .replace(/[\u0660-\u0669]/g, (d) => String(AR_DIGITS.indexOf(d)))
    .replace(/[\u06F0-\u06F9]/g, (d) => String(FA_DIGITS.indexOf(d)));
}

// ─── Input ──────────────────────────────────────────────
function Input({ value, onChange, placeholder, type = 'text', ltr, error, maxLength, autoFocus, rightSlot, suffix }) {
  const [focus, setFocus] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <input
        type={type}
        value={value ?? ''}
        onChange={(e) => onChange && onChange(toAsciiDigits(e.target.value))}
        placeholder={placeholder}
        dir={ltr ? 'ltr' : undefined}
        maxLength={maxLength}
        autoFocus={autoFocus}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        style={{
          width: '100%', boxSizing: 'border-box',
          padding: '13px 14px', paddingInlineEnd: rightSlot ? 44 : (suffix ? 52 : 14),
          fontSize: 14.5, fontFamily: 'inherit',
          background: '#fff',
          border: `1px solid ${error ? BRAND.error : focus ? BRAND.green : BRAND.border}`,
          borderRadius: 12,
          color: BRAND.text,
          outline: 'none',
          boxShadow: focus ? `0 0 0 4px ${error ? 'rgba(226,75,74,0.1)' : 'rgba(29,158,117,0.1)'}` : 'none',
          transition: 'all .15s',
          textAlign: ltr ? 'left' : undefined,
        }}
      />
      {suffix && <span style={{ position: 'absolute', insetInlineEnd: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: BRAND.muted, pointerEvents: 'none' }}>{suffix}</span>}
      {rightSlot && <div style={{ position: 'absolute', insetInlineEnd: 12, top: '50%', transform: 'translateY(-50%)' }}>{rightSlot}</div>}
    </div>
  );
}

// ─── Card ───────────────────────────────────────────────
function Card({ children, style = {}, padding = 32, elevated }) {
  return (
    <div style={{
      background: '#fff',
      border: `1px solid ${BRAND.border}`,
      borderRadius: 20,
      padding,
      boxShadow: elevated ? '0 10px 40px -10px rgba(15, 110, 86, 0.12)' : '0 1px 2px rgba(0,0,0,0.03)',
      ...style,
    }}>{children}</div>
  );
}

// ─── Progress Stepper ───────────────────────────────────
function Stepper({ steps, current }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', gap: 0, maxWidth: 720, margin: '0 auto', width: '100%' }}>
      {steps.map((s, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <React.Fragment key={i}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, minWidth: 0, flexShrink: 0 }}>
              <div style={{
                width: active ? 34 : 28, height: active ? 34 : 28,
                borderRadius: '50%',
                background: done ? BRAND.green : active ? '#fff' : BRAND.surfaceAlt,
                border: `${active ? 2 : 1}px solid ${done || active ? BRAND.green : BRAND.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: done ? '#fff' : active ? BRAND.green : BRAND.muted,
                fontWeight: 600, fontSize: 13,
                boxShadow: active ? `0 0 0 6px ${BRAND.mint}` : 'none',
                transition: 'all .25s',
                fontVariantNumeric: 'tabular-nums',
              }}>
                {done ? <Icon.Check size={14} /> : i + 1}
              </div>
              <div style={{ fontSize: 11.5, color: active ? BRAND.text : BRAND.muted, fontWeight: active ? 600 : 400, whiteSpace: 'nowrap' }}>{s}</div>
            </div>
            {i < steps.length - 1 && (
              <div style={{ flex: 1, height: 1, background: BRAND.border, margin: '14px 8px 0', minWidth: 20, position: 'relative' }}>
                <div style={{ position: 'absolute', inset: 0, background: BRAND.green, transform: `scaleX(${i < current ? 1 : 0})`, transformOrigin: 'right', transition: 'transform .35s' }}/>
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Frame (shared screen chrome) ───────────────────────
// The real LandingNavbar is rendered once by `OnboardingFlow` above the
// Frame, so Frame no longer ships its own header (the design prototype's
// fake "LIS techs / help" bar was replaced). We keep the `noHeader` prop
// as a no-op for backwards compat with any existing caller. The main
// content gets extra top padding to clear the fixed navbar (promo banner
// ~28px + nav ~64px ≈ 100px).
function Frame({ children, stepper, footer, maxWidth = 640 }) {
  return (
    <div style={{ minHeight: '100%', background: BRAND.surface, display: 'flex', flexDirection: 'column', fontFamily: 'inherit', paddingTop: 100 }}>
      {stepper && (
        <div style={{ padding: '28px 32px 8px', background: BRAND.surface, flexShrink: 0 }}>{stepper}</div>
      )}
      <main style={{ flex: 1, padding: '32px 24px 120px', display: 'flex', justifyContent: 'center', overflowY: 'auto' }}>
        <div style={{ width: '100%', maxWidth }}>{children}</div>
      </main>
      {footer && (
        <footer style={{ position: 'sticky', bottom: 0, background: 'rgba(253,252,249,0.85)', backdropFilter: 'blur(12px)', borderTop: `1px solid ${BRAND.border}`, padding: '16px 24px', display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
          <div style={{ width: '100%', maxWidth, display: 'flex', gap: 12, justifyContent: 'space-between', alignItems: 'center' }}>
            {footer}
          </div>
        </footer>
      )}
    </div>
  );
}

// ─── Chip / Badge ──────────────────────────────────────
function Chip({ children, tone = 'mint', style = {} }) {
  const tones = {
    mint: { bg: BRAND.mint, color: BRAND.greenDark },
    neutral: { bg: BRAND.surfaceAlt, color: BRAND.muted },
    error: { bg: 'rgba(226,75,74,0.08)', color: BRAND.error },
    warn: { bg: '#FFF4D6', color: '#8A5A00' },
    info: { bg: 'rgba(59,130,246,0.08)', color: BRAND.blue },
  };
  const t = tones[tone];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 999, background: t.bg, color: t.color, fontSize: 12, fontWeight: 500, ...style }}>{children}</span>
  );
}

// ─── IconChip ──────────────────────────────────────────
function IconChip({ children, size = 40 }) {
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: BRAND.mint, color: BRAND.greenDark, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{children}</div>
  );
}

export { BRAND, Logo, Icon, Button, Field, Input, Card, Stepper, Frame, Chip, IconChip };
